import { computed, inject, reactive, watch } from 'vue'
import ru from '@/locales/ru.json'
import en from '@/locales/en.json'

export const SUPPORTED_LOCALES = ['ru', 'en']
export const DEFAULT_LOCALE = 'ru'
export const I18N_KEY = Symbol('localization')

const dictionaries = { ru, en }
const state = reactive({ locale: getInitialLocale() })

function getInitialLocale() {
  if (typeof localStorage === 'undefined') return DEFAULT_LOCALE
  let saved = null
  try {
    saved = localStorage.getItem('locale')
  } catch (error) {
    // Storage can be unavailable in privacy-restricted browser contexts.
    return DEFAULT_LOCALE
  }
  return normalizeLocale(saved) || DEFAULT_LOCALE
}

function normalizeLocale(value) {
  if (!value || typeof value !== 'string') return null
  const short = value.toLowerCase().split(/[-_]/)[0]
  return SUPPORTED_LOCALES.includes(short) ? short : null
}

function resolve(dictionary, path) {
  if (Object.prototype.hasOwnProperty.call(dictionary, path)) return dictionary[path]
  const parts = path.split('.')
  let value = dictionary
  for (let index = 0; index < parts.length; index += 1) {
    if (!value || typeof value !== 'object') return undefined
    const remainder = parts.slice(index).join('.')
    if (Object.prototype.hasOwnProperty.call(value, remainder)) return value[remainder]
    value = value[parts[index]]
  }
  return value
}

function interpolate(value, params) {
  if (!params || typeof value !== 'string') return value
  return value.replace(/\{(\w+)\}/g, (_, key) => (
    Object.prototype.hasOwnProperty.call(params, key) ? String(params[key]) : `{${key}}`
  ))
}

function translate(key, params) {
  if (typeof key !== 'string') return key == null ? '' : String(key)
  const current = resolve(dictionaries[state.locale], key)
  const fallback = resolve(dictionaries[DEFAULT_LOCALE], key)
  // Legacy source strings are explicit opt-in mappings. Unknown hardcoded text
  // is intentionally returned unchanged rather than guessed or machine translated.
  const legacyKey = Object.prototype.hasOwnProperty.call(ru.legacy, key) ? `legacy.${key}` : null
  const currentValue = legacyKey ? resolve(dictionaries[state.locale], legacyKey) : current
  const value = currentValue !== undefined ? currentValue : (legacyKey ? resolve(ru, legacyKey) : fallback)
  return interpolate(value === undefined ? key : value, params)
}

function setElementText(el, key) {
  el.textContent = translate(key)
}

function applyDirective(el, binding) {
  const value = binding.value
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    if (value.key) setElementText(el, value.key)
    Object.entries(value.attrs || {}).forEach(([attribute, key]) => {
      el.setAttribute(attribute, translate(key))
    })
    return
  }
  if (typeof value === 'string') setElementText(el, value)
}

function applyDataAttributes(root) {
  if (!root || typeof root.querySelectorAll !== 'function') return
  const elements = root.matches?.('[data-i18n], [data-i18n-attrs]') ? [root] : []
  elements.push(...root.querySelectorAll('[data-i18n], [data-i18n-attrs]'))
  elements.forEach((el) => {
    if (el.dataset.i18n) setElementText(el, el.dataset.i18n)
    if (el.dataset.i18nAttrs) {
      try {
        Object.entries(JSON.parse(el.dataset.i18nAttrs)).forEach(([attribute, key]) => {
          el.setAttribute(attribute, translate(key))
        })
      } catch (error) {
        // Invalid opt-in metadata must not break rendering.
        if (process.env.NODE_ENV !== 'production') console.warn('[i18n] Invalid data-i18n-attrs', error)
      }
    }
  })
}

export const i18n = {
  state,
  locale: computed(() => state.locale),
  dictionaries,
  t: translate,
  setLocale(value) {
    const next = normalizeLocale(value) || DEFAULT_LOCALE
    state.locale = next
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem('locale', next)
      } catch (error) {
        // Locale remains reactive even when persistence is unavailable.
      }
    }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = next
      applyDataAttributes(document.body)
    }
    return next
  },
  syncUserLocale(user) {
    const preferred = user && (
      user.locale ||
      user.language ||
      user.lang ||
      user.preferredLocale ||
      user.preferredLanguage ||
      user.preferred_locale ||
      user.languageCode ||
      user.language_code ||
      user.settings?.locale ||
      user.preferences?.locale
    )
    if (preferred) i18n.setLocale(preferred)
    else if (typeof document !== 'undefined') document.documentElement.lang = state.locale
    return state.locale
  },
  formatDate(value, options = {}) {
    if (value === null || value === undefined || value === '') return ''
    return new Intl.DateTimeFormat(state.locale, options).format(new Date(value))
  },
  formatNumber(value, options = {}) {
    if (value === null || value === undefined || value === '') return ''
    return new Intl.NumberFormat(state.locale, options).format(Number(value))
  },
  formatCurrency(value, currency = 'RUB', options = {}) {
    return i18n.formatNumber(value, {
      style: 'currency',
      currency,
      currencyDisplay: 'symbol',
      ...options
    })
  },
  localizeElement(element) {
    applyDataAttributes(element)
  },
  install(app) {
    app.provide(I18N_KEY, i18n)
    app.config.globalProperties.$t = translate
    app.config.globalProperties.$locale = i18n.locale
    app.config.globalProperties.$formatDate = i18n.formatDate
    app.config.globalProperties.$formatNumber = i18n.formatNumber
    app.config.globalProperties.$formatCurrency = i18n.formatCurrency
    app.directive('i18n', {
      mounted: applyDirective,
      updated: applyDirective
    })
    app.directive('i18n-attr', {
      mounted(el, binding) {
        Object.entries(binding.value || {}).forEach(([attribute, key]) => {
          el.setAttribute(attribute, translate(key))
        })
      },
      updated(el, binding) {
        Object.entries(binding.value || {}).forEach(([attribute, key]) => {
          el.setAttribute(attribute, translate(key))
        })
      }
    })
    if (typeof document !== 'undefined') {
      document.documentElement.lang = state.locale
      const localize = () => applyDataAttributes(document.body)
      localize()
      watch(() => state.locale, localize)
    }
  }
}

export function syncUserLocale(user) {
  return i18n.syncUserLocale(user)
}

export const locale = i18n.locale
export const t = i18n.t
export const setLocale = (value) => i18n.setLocale(value)
export const formatDate = (...args) => i18n.formatDate(...args)
export const formatNumber = (...args) => i18n.formatNumber(...args)
export const formatCurrency = (...args) => i18n.formatCurrency(...args)

export function useI18n() {
  return inject(I18N_KEY, i18n)
}

export default i18n